/* findings.js — Catalogue des constats et assemblage des écarts. Tout libellé,
 * explication et gravité vit ici ; détecteurs et règles n'ajoutent que des
 * valeurs. Écart : { type, emitter, domain, subject, severity, expected?,
 * observed, character, confidence?, proof?, correction?, actions, message,
 * priority }. Expose self.MediaFindings. */
'use strict';

import ACTIONS_MOD from './actions.js';

var DOMAINS = {
  image: 'Image',
  audio: 'Audio',
  integrity: 'Intégrité',
  structure: 'Structure',
  tracks: 'Pistes et métadonnées',
  coherence: 'Cohérence'
};

var PRIORITIES = {
  fix: 'À corriger avant usage',
  check: 'À vérifier',
  optional: 'Amélioration facultative'
};
var PRIORITY_ORDER = ['fix', 'check', 'optional'];

var CORRECTIONS = {
  metadata: 'Métadonnées (sans perte)',
  remux: 'Remux (sans perte)',
  transform: 'Transformation (réencodage)',
  impossible: 'Impossible (information perdue)',
  none: 'Sans objet'
};

var ORIGINS = { declared: 'déclaré', measured: 'mesuré' };

var SEVERITIES = { error: 'Erreur', warning: 'Avertissement', info: 'Information' };

// Textes de présentation des écarts (fiche, rapport, résumé copié, lot).
var LABELS = {
  gaps: 'Écarts',
  expected: 'Attendu',
  observed: 'Observé',
  why: 'Pourquoi',
  correction: 'Correction',
  confidence: 'Confiance',
  file: 'Fichier',
  absent: 'absent',
  yes: 'oui',
  no: 'non',
  emptyWithProfile: 'Aucun écart : les règles du profil sont satisfaites et les contrôles automatiques n’ont rien relevé.',
  emptyWithoutProfile: 'Aucun écart relevé par les contrôles automatiques.',
  rulesPassed: 'Règles satisfaites',
  rulesUnevaluable: 'Règles non évaluables',
  rulesNotApplicable: 'Règles sans objet (condition non remplie)',
  sources: 'Sources',
  consulted: 'consultée le {date}',
  elementSuffix: ' (élément « {name} »)',
  proof: 'Preuve',
  proofStructure: 'élément « {name} » à l’octet {offset}, dans la structure du conteneur',
  showInStructure: 'Voir dans la structure',
  privacyTitle: 'Métadonnées personnelles',
  personalBadge: 'personnel',
  privacyNote: 'Ce que le fichier révèle sur la personne ou l’appareil qui l’a produit. Descriptions et crédits (artiste, compositeur, droits) sont listés pour information.',
  privacyEmpty: 'Aucune métadonnée personnelle trouvée : ni position, ni appareil, ni auteur, ni commentaire, ni date, ni logiciel.',
  position: 'latitude {lat}, longitude {lon}{alt}',
  altitude: ', altitude {alt} m',
  blockOrigin: 'Valeurs déclarées par le fichier, sauf mention contraire.',
  measureTitle: 'Mesures sur les paquets',
  measureLede: 'Ce que le fichier déclare n’engage que lui. Ces valeurs-ci sont lues sur les paquets eux-mêmes : débit réel, cadence réellement observée, intervalle entre images clés, décalage de départ entre l’image et le son. Rien n’est décodé, donc c’est rapide, même sur plusieurs gigaoctets.',
  measureRun: 'Mesurer',
  measureAgain: 'Mesurer à nouveau',
  measuring: 'Mesure en cours…',
  measureCancel: 'Annuler',
  loudnessRun: 'Mesurer le niveau sonore',
  loudnessAgain: 'Mesurer à nouveau le niveau sonore',
  loudnessNote: 'Le niveau sonore demande de décoder l’audio : comptez une dizaine de secondes pour un film. Mesuré selon ITU-R BS.1770, comme les diffuseurs et les plateformes.',
  pictureRun: 'Analyser l’image',
  pictureAgain: 'Analyser l’image à nouveau',
  pictureNote: 'Quelques images sont décodées à huit endroits du fichier, pour voir ce que la fiche technique ne dit pas : les bandes noires — donc le vrai format de l’image — et l’entrelacement réel, qui contredit souvent le balayage déclaré.',
  pictureNone: 'Aucune image à analyser dans ce fichier.',
  scanProgressive: 'progressif',
  scanInterlaced: 'entrelacé',
  scanTelecine: 'télécinéma (3:2)',
  scanPartial: 'entrelacement sur une partie des images',
  scanStill: 'image trop immobile pour conclure',
  notMeasured: 'non évaluable : {reason}',
  noGapInDomain: 'Aucun écart.',
  notCorrectable: '{count} autre(s) écart(s) sans correction automatique : leur explication est dans « Écarts ».',
  fixesTitle: 'Corriger',
  fixIt: 'Corriger',
  gapsLede: 'Ce qui s’écarte des bonnes pratiques et, si vous en avez choisi un, de l’usage visé. Pour chaque écart : le constat, pourquoi il compte, et la correction possible.',
  removePersonal: 'Retirer ces métadonnées →',
  fixesChip: 'correction(s) possible(s)',
  orderBy: 'Ranger',
  byPriority: 'par priorité',
  byDomain: 'par domaine',
  usageTitle: 'Conformité à un usage',
  notChecked: 'Ce que ce profil ne contrôle pas',
  usageNone: 'Aucun usage choisi : seuls les contrôles automatiques s’appliquent.',
  nothingToCorrect: 'Aucun écart corrigeable pour ce fichier.'
};

// Ce que le contrôle examine dans chaque domaine, et ce qu'il n'examine pas.
var DOMAIN_SCOPE = {
  image: 'Caractéristiques déclarées de la vidéo contrôlées. Le contenu de l’image n’est pas encore analysé : « Analyser l’image », dans les mesures, décode quelques images pour voir les bandes noires et l’entrelacement réel.',
  imageMeasured: 'Caractéristiques déclarées de la vidéo contrôlées, et image décodée : bandes noires et entrelacement constatés sur les images elles-mêmes.',
  audio: 'Caractéristiques déclarées de l’audio contrôlées. Le son n’est pas encore analysé : « Mesurer le niveau sonore », dans les mesures, le décode pour en donner le niveau et la crête vraie.',
  audioMeasured: 'Caractéristiques déclarées de l’audio contrôlées, et son décodé : niveau sonore intégré, plage et crête vraie.',
  integrity: 'Structure du conteneur lue sur le fichier entier.',
  integritySurvey: 'Premier niveau de la structure lu sur le fichier entier ; l’exploration complète se lance dans « Détails techniques ».',
  integrityNone: 'Structure non lue : lancez l’exploration dans « Détails techniques » pour contrôler l’intégrité.',
  structure: 'Conteneur et combinaison des codecs contrôlés.',
  tracks: 'Langues, drapeaux et disposition des pistes contrôlés.',
  coherence: 'Cohérence entre valeurs déclarées contrôlée.'
};

// Leur présence dit qu'un domaine a été mesuré, et pas seulement lu.
var MEASURED_GROUPS = {
  image: ['activeWidth', 'activeHeight', 'activeAspectRatio', 'barsShare', 'scanType', 'telecine'],
  audio: ['loudnessIntegrated', 'loudnessRange', 'truePeak']
};

var TRACK_NAMES = { video: 'Vidéo', audio: 'Piste audio', text: 'Sous-titre', image: 'Image', menu: 'Menu', other: 'Piste' };
var ORDINAL = ' n°{n}';

/* ---------- Catalogue des constats ----------
 * message : texte historique du constat, gabarit à {paramètres}.
 * confidence : pour les constats heuristiques, niveau et raisons (gabarits). */
var CATALOG = {
  'av-duration': {
    label: 'Durées audio et vidéo différentes',
    explanation: 'Les pistes audio et vidéo ne déclarent pas la même durée. Un écart important annonce souvent une désynchronisation ou une piste coupée.',
    message: 'Écart de durée entre vidéo ({video}) et audio ({audio}) — vérifier la synchronisation.',
    domain: 'coherence', severity: 'warning', character: 'deterministic', correction: 'none',
    noAction: 'Rien à corriger dans le fichier tant que la lecture ne montre pas de décalage.'
  },
  'no-lang': {
    label: 'Langue de piste absente',
    explanation: 'La piste ne déclare pas de langue. Les lecteurs ne peuvent pas la choisir automatiquement selon les préférences de langue.',
    message: '{track} sans langue déclarée.',
    domain: 'tracks', severity: 'info', character: 'deterministic', correction: 'metadata',
    actions: ['set-language']
  },
  'multi-default': {
    label: 'Plusieurs pistes audio par défaut',
    explanation: 'Plusieurs pistes audio portent le drapeau « par défaut ». Le lecteur choisit alors selon ses propres règles, pas forcément la piste voulue.',
    message: '{count} pistes audio marquées « par défaut » — un seul défaut est attendu.',
    domain: 'tracks', severity: 'info', character: 'deterministic', correction: 'metadata',
    actions: ['single-default']
  },
  'unusual-combo': {
    label: 'Combinaison conteneur et codec inhabituelle',
    explanation: 'Ce codec est rarement placé dans ce conteneur. Certains lecteurs peuvent refuser le fichier.',
    message: 'Combinaison inhabituelle : {codec} dans un conteneur {container}.',
    domain: 'structure', severity: 'warning', character: 'heuristic', correction: 'remux',
    actions: ['change-container'],
    confidence: { level: 'low', reasons: ['la liste des combinaisons usuelles n’est pas exhaustive'] }
  },
  'low-bitrate': {
    label: 'Débit vidéo bas pour la définition',
    explanation: 'Le débit rapporté à la définition et à la cadence est faible. Des artefacts de compression sont probables.',
    message: 'Débit vidéo anormalement bas pour la définition (risque d’artefacts).',
    domain: 'image', severity: 'warning', character: 'heuristic', correction: 'impossible',
    remedy: 'Le détail perdu à l’encodage ne peut pas être recréé. Chercher une source mieux encodée.',
    confidence: { level: 'medium', reasons: ['{bpp} bit par pixel et par image, sous le seuil de {threshold}', 'le seuil ne tient compte ni du codec ni du contenu'] }
  },
  'high-bitrate': {
    label: 'Débit vidéo élevé pour la définition',
    explanation: 'Le débit rapporté à la définition et à la cadence est élevé. Le fichier est plus volumineux que nécessaire pour un usage courant.',
    message: 'Débit vidéo très élevé pour la définition (fichier volumineux).',
    domain: 'image', severity: 'info', character: 'heuristic', correction: 'transform',
    actions: ['reencode'],
    confidence: { level: 'medium', reasons: ['{bpp} bit par pixel et par image, au-dessus du seuil de {threshold}', 'le seuil ne tient compte ni du codec ni du contenu'] }
  },
  'hdr-bitdepth': {
    label: 'HDR déclaré sous 10 bits',
    explanation: 'Un format HDR est déclaré, mais la vidéo est en moins de 10 bits. Le HDR suppose au moins 10 bits ; la déclaration ou l’encodage est incohérent.',
    message: 'HDR déclaré ({hdr}) mais profondeur de {depth} bits — le HDR attend au moins 10 bits.',
    domain: 'coherence', severity: 'warning', character: 'deterministic', correction: 'impossible',
    remedy: 'La précision manquante ne peut pas être recréée. Chercher une source en 10 bits.'
  },
  'no-layout': {
    label: 'Disposition des canaux absente',
    explanation: 'La piste a plus de deux canaux sans déclarer leur disposition. Le lecteur doit deviner quel canal va sur quelle enceinte.',
    message: 'Disposition des canaux non déclarée sur une piste audio à {channels} canaux.',
    domain: 'tracks', severity: 'info', character: 'deterministic', correction: 'transform',
    noAction: 'La disposition réelle ne se déduit pas du fichier : elle se précise au réencodage, en connaissant la source.'
  },
  'dar-mismatch': {
    label: 'Ratio d’affichage incohérent',
    explanation: 'Le ratio d’affichage déclaré ne correspond pas à la définition, et aucun ratio de pixel ne l’explique. L’image risque d’être déformée.',
    message: 'Ratio d’affichage ({dar}) incohérent avec la définition {width}×{height} ({geometry}).',
    domain: 'coherence', severity: 'info', character: 'deterministic', correction: 'metadata',
    noAction: 'Le bon ratio ne se déduit pas du fichier : regarder l’image avant de corriger la déclaration.'
  },
  'interlaced-content': {
    label: 'Contenu entrelacé, non déclaré',
    explanation: 'Les images décodées portent un peigne : elles sont faites de deux demi-images prises à des instants différents. Le fichier, lui, ne l’annonce pas. Un lecteur qui se fie à la déclaration affichera donc le peigne tel quel, visible dès qu’il y a du mouvement. Ce constat vient des images elles-mêmes, pas du champ « type de balayage ».',
    message: 'Les images décodées sont entrelacées, ce que le fichier ne déclare pas.',
    domain: 'image', severity: 'warning', character: 'deterministic', correction: 'transform',
    actions: ['deinterlace']
  },
  'telecine-content': {
    label: 'Film télécinématographié, non déclaré',
    explanation: 'Deux images sur cinq portent un peigne, toujours aux mêmes places du cycle : c’est la signature d’un film à 24 images transposé en vidéo à 30, procédé dit « 3:2 ». Le contenu d’origine est intact et récupérable. Il ne faut surtout pas le désentrelacer, ce qui mélangerait les demi-images et coûterait de la netteté : il faut défaire le télécinéma, ce qui rend au film ses 24 images exactes.',
    message: 'Les images décodées suivent le cycle 3:2 du télécinéma, ce que le fichier ne déclare pas.',
    domain: 'image', severity: 'warning', character: 'deterministic', correction: 'transform',
    actions: ['inverse-telecine']
  },
  'bars-both-axes': {
    label: 'Bandes noires sur les quatre côtés',
    explanation: 'L’image utile est entourée de noir en haut, en bas et sur les côtés : le cadre a été posé deux fois, une image déjà bordée ayant été remise à l’échelle dans un autre cadre. La définition annoncée n’est plus celle de l’image, et le poids du fichier sert en partie à coder du noir.',
    message: 'Image utile de {width}×{height} dans un cadre de {frameWidth}×{frameHeight}.',
    domain: 'image', severity: 'warning', character: 'deterministic', correction: 'transform',
    noAction: 'Recadrer suppose de réencoder, donc une perte. Mieux vaut repartir de la source et ne l’encadrer qu’une fois.'
  },
  'truncated': {
    label: 'Fin de fichier tronquée',
    explanation: 'Un élément du conteneur annonce plus de données que le fichier n’en contient : le fichier a été coupé, par exemple par une copie ou un téléchargement interrompu. La fin est illisible.',
    message: 'Structure incomplète à l’octet {offset}{element} : le fichier s’arrête avant la fin annoncée.',
    domain: 'integrity', severity: 'error', character: 'deterministic', correction: 'impossible',
    remedy: 'Les données manquantes ne peuvent pas être recréées. Retrouver une copie complète du fichier.'
  },
  'invalid-structure': {
    label: 'Élément de structure invalide',
    explanation: 'Un élément du conteneur déclare une taille impossible ou ne peut pas être lu. Les lecteurs risquent de s’arrêter à cet endroit.',
    message: 'Élément invalide à l’octet {offset}{element}.',
    domain: 'integrity', severity: 'error', character: 'deterministic', correction: 'remux',
    noAction: 'Un remux réécrit la structure, mais les données situées après l’anomalie peuvent être perdues : aucune commande n’est proposée sans vérification.'
  },
  'moov-late': {
    label: 'Index (moov) placé en fin de fichier',
    explanation: 'L’index du fichier est écrit après les données. Un lecteur en ligne doit tout télécharger avant de commencer ; en lecture locale, cela ne gêne pas.',
    message: 'Index (moov) placé après les données (mdat).',
    domain: 'structure', severity: 'info', character: 'deterministic', correction: 'remux',
    actions: ['faststart']
  },
  'personal-metadata': {
    label: 'Métadonnées personnelles présentes',
    explanation: 'Le fichier contient des informations sur la personne ou l’appareil qui l’a produit. Elles voyagent avec le fichier quand on le partage.',
    message: 'Métadonnées personnelles présentes : {list}.',
    domain: 'tracks', severity: 'info', character: 'deterministic', correction: 'metadata',
    actions: ['clean-metadata']
  },
  'profile-rule': {
    label: 'Règle de profil non satisfaite',
    explanation: 'Le fichier ne satisfait pas la règle « {rule} » du profil {profile} v{version}.',
    domain: null, severity: null, character: 'deterministic',
    noAction: 'Aucune commande n’est proposée pour cette règle : la correction dépend de la source et de l’usage.'
  }
};

// Libellés, et types qui déclenchent le constat.
var PRIVACY_KINDS = {
  location: 'Position', device: 'Appareil', author: 'Auteur', comment: 'Commentaire', description: 'Description',
  software: 'Logiciel', date: 'Date', credit: 'Crédit'
};
var PRIVACY_PERSONAL = ['location', 'device', 'author', 'comment'];

var CONFIDENCE_LEVELS = { high: 'élevée', medium: 'moyenne', low: 'faible' };

/* ---------- Attendu d'une règle ---------- */
var OPS = {
  exists: 'présent',
  absent: 'absent',
  eq: 'égal à {value}',
  ne: 'différent de {value}',
  gt: 'supérieur à {value}',
  gte: 'au moins {value}',
  lt: 'inférieur à {value}',
  lte: 'au plus {value}',
  contains: 'contient « {value} »',
  notContains: 'ne contient pas « {value} »',
  inList: 'l’une des valeurs {value}',
  range: 'entre {min} et {max}',
  tolerance: '{target} à {tol} près',
  ratio: 'égal à {num} / {den}, à {tolPct} % près'
};
var CONDITION = 'si {cond}';
var MEASURED_SUFFIX = ' (valeur mesurée)';
var QUANTIFIERS = {
  all: 'sur chaque piste',
  any: 'sur au moins une piste',
  none: 'sur aucune piste',
  atLeast: 'sur au moins {count} piste(s)',
  atMost: 'sur au plus {count} piste(s)',
  exactly: 'sur exactement {count} piste(s)'
};
var TRACK_KIND_LABELS = { container: 'Conteneur', video: 'Vidéo', audio: 'Audio', text: 'Sous-titres', image: 'Image', menu: 'Chapitres / Menu' };

var FIELDS = {
  formatRaw: 'Format',
  format: 'Format',
  formatProfile: 'Profil',
  codecId: 'Codec ID',
  duration: 'Durée',
  bitRate: 'Débit',
  bitRateMode: 'Mode de débit',
  overallBitRate: 'Débit global',
  width: 'Largeur',
  height: 'Hauteur',
  displayAspectRatio: 'Ratio d’affichage',
  pixelAspectRatio: 'Ratio de pixel',
  frameRate: 'Images/seconde',
  frameRateMode: 'Mode de cadence',
  scanType: 'Type de balayage',
  bitDepth: 'Profondeur',
  colorSpace: 'Espace colorimétrique',
  chromaSubsampling: 'Sous-échantillonnage chroma',
  hdrFormat: 'Format HDR',
  colorRange: 'Plage de couleur',
  colorPrimaries: 'Primaires',
  transferCharacteristics: 'Transfert (courbe)',
  matrixCoefficients: 'Matrice',
  channels: 'Canaux',
  'channelLayout.raw': 'Disposition',
  samplingRate: 'Fréquence d’échantillonnage',
  'language.code': 'Langue',
  title: 'Titre',
  default: 'Par défaut',
  forced: 'Forcés',
  moovPosition: 'Position de l’index (moov)',
  fileSize: 'Taille du fichier',
  'counts.video': 'Nombre de pistes vidéo',
  'counts.audio': 'Nombre de pistes audio',
  'counts.text': 'Nombre de sous-titres',
  formatLevel: 'Niveau',
  keyframeInterval: 'Intervalle entre images clés',
  peakBitRate: 'Débit de pointe',
  avOffset: 'Décalage audio/vidéo',
  loudnessIntegrated: 'Niveau sonore intégré',
  loudnessRange: 'Plage de loudness',
  truePeak: 'Crête vraie',
  activeWidth: 'Largeur de l’image utile',
  activeHeight: 'Hauteur de l’image utile',
  activeAspectRatio: 'Format de l’image utile',
  barsShare: 'Part des bandes noires',
  telecine: 'Télécinéma 3:2',
  'privacy.location': 'Position',
  'privacy.device': 'Appareil',
  'privacy.author': 'Auteur',
  'privacy.comment': 'Commentaire',
  truncated: 'Fichier tronqué',
  browserContainer: 'Conteneur reconnu par ce navigateur',
  browserDecode: 'Décodable par ce navigateur',
  browserSmooth: 'Lecture fluide sur ce navigateur'
};
// Champs rangés dans « Pistes et métadonnées » quel que soit le type de piste.
var TRACK_METADATA_FIELDS = { 'language.code': 1, title: 1, default: 1, forced: 1, 'channelLayout.raw': 1 };
// Nature de correction d'une règle de profil, selon le champ contrôlé.
var CORRECTION_BY_FIELD = {
  'language.code': 'metadata', title: 'metadata', default: 'metadata', forced: 'metadata',
  displayAspectRatio: 'metadata', 'channelLayout.raw': 'metadata'
};
var DOMAIN_BY_KIND = { container: 'structure', video: 'image', audio: 'audio', text: 'tracks', image: 'tracks', menu: 'tracks' };

function fill(tpl, params) {
  return String(tpl).replace(/\{(\w+)\}/g, function (m, k) {
    return params && params[k] != null ? String(params[k]) : m;
  });
}

function rawText(property, v) {
  if (v === true) return LABELS.yes;
  if (v === false) return LABELS.no;
  return String(v);
}

// fmt(property, valeur) met en forme ; sans lui, valeurs telles quelles.
function expectedText(e, fmt) {
  fmt = fmt || rawText;
  function one(v) { return fmt(e.field, v); }
  var v = e.value;
  var params = { value: Array.isArray(v) ? v.map(one).join(', ') : (v === undefined || (v && typeof v === 'object') ? '' : one(v)) };
  if (v && typeof v === 'object' && !Array.isArray(v)) {
    params.min = one(v.min); params.max = one(v.max); params.target = one(v.target); params.tol = one(v.tol);
    if (e.op === 'ratio') {
      params.num = FIELDS[v.num] || v.num; params.den = FIELDS[v.den] || v.den;
      params.tolPct = String(Math.round(v.tol * 1000) / 10).replace('.', ',');
    }
  }
  var text = (FIELDS[e.field] || e.field) + (e.origin === 'measured' ? MEASURED_SUFFIX : '') + ' : ' + fill(OPS[e.op] || (e.op + ' {value}'), params);
  if (e.track !== 'container') {
    text += ' — ' + (TRACK_KIND_LABELS[e.track] || e.track) + ', ' + fill(QUANTIFIERS[e.quantifier] || QUANTIFIERS.all, { count: e.count });
  }
  if (e.when) {
    var conds = (Array.isArray(e.when) ? e.when : [e.when]).map(function (c) {
      return expectedText({ field: c.field, op: c.op, value: c.value, track: 'container' }, fmt);
    });
    text += ', ' + fill(CONDITION, { cond: conds.join(' et ') });
  }
  return text;
}

function expected(rule) {
  var out = { field: rule.field, op: rule.op, track: rule.track };
  if (rule.value !== undefined) out.value = rule.value;
  if (rule.when) out.when = rule.when;
  if (rule.origin) out.origin = rule.origin;
  if (rule.track !== 'container') {
    out.quantifier = rule.quantifier || 'all';
    if (rule.count != null) out.count = rule.count;
  }
  out.text = expectedText(out);
  return out;
}

/* ---------- Valeurs et sujets ---------- */
function subjectOf(t) {
  if (!t || t.kind == null) return { kind: 'container' };
  return { kind: t.kind, ordinal: t.ordinal, id: t.id != null ? t.id : null };
}

function value(subject, property, v) {
  return { subject: subject, property: property, value: v == null ? null : v, origin: 'declared', source: 'mediainfo' };
}

function trackName(t, total) {
  return TRACK_NAMES[t.kind] + (total > 1 ? fill(ORDINAL, { n: t.ordinal }) : '');
}

function message(type, params) {
  var c = CATALOG[type];
  return c && c.message ? fill(c.message, params) : type;
}

function confidence(type, params) {
  var c = CATALOG[type] && CATALOG[type].confidence;
  if (!c) return null;
  return { level: c.level, reasons: c.reasons.map(function (r) { return fill(r, params); }) };
}

/* ---------- Priorité d'affichage ---------- */
function priority(severity, character) {
  if (severity === 'error' && character === 'deterministic') return 'fix';
  if (character === 'heuristic' || severity === 'warning' || severity === 'error') return 'check';
  return 'optional';
}

/* ---------- Écarts ---------- */

function actionsFor(gap, model, failing) {
  var c = CATALOG[gap.type] || {};
  if (!model || !ACTIONS_MOD || gap.correction === 'impossible') return [];
  if (gap.type !== 'profile-rule' && !c.actions) return [];
  return ACTIONS_MOD.propose(gap, { model: model, failing: failing || [] });
}

function fromDiagnostic(d) {
  var c = CATALOG[d.code] || {};
  var character = d.character || c.character || 'deterministic';
  var severity = d.level;
  var gap = {
    type: d.code,
    emitter: { kind: 'detector' },
    domain: c.domain || null,
    subject: d.subject || { kind: 'container' },
    severity: severity,
    observed: d.observed || [],
    character: character,
    actions: [],
    message: d.message
  };
  if (d.confidence) gap.confidence = d.confidence;
  if (d.proof) gap.proof = d.proof;
  if (c.correction) gap.correction = c.correction;
  gap.priority = priority(severity, character);
  return gap;
}

function fromRule(r, report) {
  var field = r.field;
  var kind = r.track;
  var gap = {
    type: 'profile-rule',
    emitter: { kind: 'rule', profile: { id: report.profile.id, version: report.profile.version }, rule: r.id },
    domain: TRACK_METADATA_FIELDS[field] ? 'tracks' : (DOMAIN_BY_KIND[kind] || 'structure'),
    subject: r.subject || { kind: 'container' },
    severity: r.status,
    expected: r.expected,
    observed: r.observedValues || [],
    character: 'deterministic',
    correction: ruleCorrection(r),
    actions: [],
    message: r.message
  };
  gap.priority = priority(gap.severity, gap.character);
  return gap;
}

function ruleCorrection(r) {
  if (CORRECTION_BY_FIELD[r.field]) return CORRECTION_BY_FIELD[r.field];
  if (r.track === 'container') return 'remux';
  // présence ou absence d'une piste : l'ajouter ou la retirer
  if (r.expected && (r.expected.op === 'exists' || r.expected.op === 'absent')) return 'remux';
  return 'transform';
}

/* Constats des détecteurs et règles en écart, avec leurs actions si le modèle
 * est fourni. input : { diagnostics, report, model }. Tri par priorité. */
function collect(input) {
  var gaps = [];
  (input.diagnostics || []).forEach(function (d) {
    var g = fromDiagnostic(d);
    g.actions = actionsFor(g, input.model);
    gaps.push(g);
  });
  var rep = input.report;
  if (rep && rep.applicable && rep.applicable.ok) {
    rep.results.forEach(function (r) {
      if (r.status !== 'warning' && r.status !== 'error') return;
      var g = fromRule(r, rep);
      g.actions = actionsFor(g, input.model, r.failing);
      gaps.push(g);
    });
  }
  var rank = {};
  PRIORITY_ORDER.forEach(function (p, i) { rank[p] = i; });
  return gaps.map(function (g, i) { return { g: g, i: i }; })
    .sort(function (a, b) { return (rank[a.g.priority] - rank[b.g.priority]) || (a.i - b.i); })
    .map(function (x) { return x.g; });
}

var api = {
  CATALOG: CATALOG, DOMAINS: DOMAINS, PRIORITIES: PRIORITIES, PRIORITY_ORDER: PRIORITY_ORDER,
  CORRECTIONS: CORRECTIONS, ORIGINS: ORIGINS, CONFIDENCE_LEVELS: CONFIDENCE_LEVELS, FIELDS: FIELDS,
  SEVERITIES: SEVERITIES, LABELS: LABELS, DOMAIN_SCOPE: DOMAIN_SCOPE, MEASURED_GROUPS: MEASURED_GROUPS,
  TRACK_KIND_LABELS: TRACK_KIND_LABELS,
  PRIVACY_KINDS: PRIVACY_KINDS, PRIVACY_PERSONAL: PRIVACY_PERSONAL,
  fill: fill, message: message, confidence: confidence, expected: expected, expectedText: expectedText,
  subjectOf: subjectOf, value: value, trackName: trackName,
  priority: priority, collect: collect
};
globalThis.MediaFindings = api;
export default api;
